import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Drawer,
  DrawerFooter,
  ErrorBanner,
  InfoTip,
  type BadgeTone,
} from '@zarya/ui';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import { useDecision, type DecisionDetail } from './Decisions.api';
import { causaOf } from './causa';
import { replayClaims } from './replayClaims';
import { WhyDenied } from './WhyDenied';
import { DecisionTargetCell } from './DecisionTargetCell';
import {
  a2aMethod,
  inferredTip,
  verdict as verdictBadge,
  decisionIdentity,
  decisionKind,
  decisionPerson,
  decisionTarget,
  isControlCall,
  verdictInferred,
  type Decision,
} from './vocabulario';
import s from './Decisions.module.css';

/**
 * T17 — drawer da decisão (FR-099). Abre por `?drawer=<request_id>` na rota de
 * T16 (e a partir de T3, que navega para cá com o parâmetro já posto).
 *
 * O que o operador vem buscar aqui é uma coisa: **por que** esta chamada passou
 * ou não. Daí a ordem — veredito e motivo primeiro, depois quem chamou, quem era
 * a pessoa, o vínculo entre as duas, e só então o pedido e o resultado.
 */

/** `TriEstado` do vínculo token↔workload: `null` é "não deu para comparar". */
const LINK_TOM: Record<'confere' | 'nao_confere' | 'indeterminado', BadgeTone> = {
  confere: 'success',
  nao_confere: 'danger',
  indeterminado: 'neutral',
};

/** Função, e não tabela: uma tabela com `t()` é avaliada na importação. */
function linkState(confere: boolean | null | undefined) {
  const chave = confere === true ? 'confere' : confere === false ? 'nao_confere' : 'indeterminado';
  return { rotulo: t(`decisions.drawer.binding.${chave}`), tone: LINK_TOM[chave] };
}

function Section({
  titulo,
  children,
  nota,
  testId,
  state,
}: {
  titulo: string;
  children: ReactNode;
  /** Uma frase sobre a seção inteira, entre o título e os campos (o estado do bloco Pessoa). */
  nota?: string;
  testId?: string;
  state?: string;
}) {
  return (
    <section className={s.secao} data-testid={testId} data-state={state}>
      <h3 className={s.secaoTitulo}>{titulo}</h3>
      {nota ? <p className={s.muted}>{nota}</p> : null}
      <dl className={s.defs}>{children}</dl>
    </section>
  );
}

/**
 * Os campos da pessoa verificada: as claims, a validade e o vínculo do token com o certificado. Na linha que não diz
 * (guardada antes do aim-6ph.8), os mesmos campos dizem «não informado».
 */
function PersonFields({ datum }: { datum: DecisionDetail }) {
  const vinculo = datum.user?.binding;
  const stateLink = linkState(vinculo?.matches);
  return (
    <>
      <Def rotulo={t('decisions.drawer.field.sub')} valor={datum.user?.sub} mono />
      <Def rotulo={t('decisions.drawer.field.profile')} valor={datum.user?.profile} />
      <Def rotulo={t('decisions.drawer.field.issuer')} valor={datum.user?.issuer} mono />
      <Def
        rotulo={t('decisions.drawer.field.audience')}
        valor={datum.user?.audience?.join(', ')}
        mono
      />
      <Def
        rotulo={t('decisions.drawer.field.expiresAt')}
        valor={dataHour(datum.user?.expires_at)}
      />
      <Def
        rotulo={t('decisions.drawer.field.binding')}
        tip={t('decisions.drawer.field.binding.tip')}
        valor={
          // Num detalhe DEGRADADO não há bloco de vínculo nenhum, e o
          // tri-estado não serve: «indeterminado» significa «o PDP comparou
          // e não deu», que é uma afirmação sobre a decisão. Aqui a verdade
          // é outra — ninguém tem o registro da comparação. Um badge no
          // lugar de «não informado» transformaria a falta do dado num
          // resultado da checagem, exatamente o que o aviso acima nega.
          datum.detail_unavailable && !vinculo ? null : (
            // Sem o motivo ao lado do selo (T14): os dois SPIFFE IDs abaixo mostram a diferença.
            <span className={s.vinculo}>
              <Badge tone={stateLink.tone}>{stateLink.rotulo}</Badge>
            </span>
          )
        }
      />
      {vinculo?.spiffe_id_in_token || vinculo?.spiffe_id_from_certificate ? (
        <>
          <Def
            rotulo={t('decisions.drawer.field.spiffeIdInToken')}
            valor={vinculo?.spiffe_id_in_token}
            mono
          />
          <Def
            rotulo={t('decisions.drawer.field.spiffeIdFromCertificate')}
            valor={vinculo?.spiffe_id_from_certificate}
            mono
          />
        </>
      ) : null}
    </>
  );
}

type PersonState = NonNullable<DecisionDetail['person_state']>;

/** O nome de cada estado do bloco Pessoa (D14 e T17 do mrolim): chaves, e não textos, para o idioma valer na hora. */
const PERSON_STATE_KEY: Record<PersonState, string> = {
  verified: 'decisions.drawer.person.verified',
  no_token: 'decisions.drawer.person.noToken',
  unverified: 'decisions.drawer.person.unverified',
  described: 'decisions.drawer.person.described',
};

/** A frase do estado, quando ele não tem pessoa verificada a mostrar. */
const PERSON_STATE_TEXT: Partial<Record<PersonState, string>> = {
  no_token: 'decisions.drawer.person.noToken.text',
  unverified: 'decisions.drawer.person.unverified.text',
  described: 'decisions.drawer.person.described.text',
};

function Def({
  rotulo,
  valor,
  mono,
  tip,
}: {
  rotulo: string;
  valor: ReactNode;
  mono?: boolean;
  tip?: string;
}) {
  return (
    <>
      <dt>
        {rotulo}
        {tip ? (
          <InfoTip text={tip} label={t('common.ui.about', { label: rotulo })} size="xs" />
        ) : null}
      </dt>
      <dd className={mono ? s.mono : undefined}>
        {valor ?? <span className={s.vazio}>{t('decisions.drawer.notInformed')}</span>}
      </dd>
    </>
  );
}

/** O caso da frase do veredito, que o `data-case` da linha expõe aos testes. */
type VerdictCase =
  | 'ruleAndContract'
  | 'delegation'
  | 'ruleMissing'
  | 'controlCall'
  | 'session'
  | 'ungoverned'
  | 'reason'
  | 'noReason'
  | 'none';

/**
 * A frase do veredito (D8 do mrolim, §3.1 do protótipo do aim-6ph.4): no ALLOW, «Permitido por», que descreve a regra
 * `allow` do Rego que vale para o tipo da decisão; na negação, «Motivo», com a frase do PDP.
 *
 * O ALLOW se lê da LINHA da lista (tipo, regra, causa e identidade). A frase do PDP de uma permitida não diz o que
 * permitiu, e «sem motivo registrado» ali soava como falha. A chamada de controle (aim-6ph.8) vem antes da sessão: ela
 * também não pede tool, e quem a libera é a identidade de descoberta do executor, não as regras.
 */
function allowedBy(decision: Decision): { key: VerdictCase; text: string } {
  if (causaOf(decision.cause) === 'ungoverned_allowed') {
    return { key: 'ungoverned', text: t('decisions.drawer.allowedBy.ungoverned') };
  }
  if (isControlCall(decision)) {
    return { key: 'controlCall', text: t('decisions.drawer.allowedBy.controlCall') };
  }
  const kind = decisionKind(decision);
  if (kind === 'session') return { key: 'session', text: t('decisions.drawer.allowedBy.session') };
  const identity = decisionIdentity(decision).texto;
  if (!decision.rule) {
    return { key: 'ruleMissing', text: t('decisions.drawer.allowedBy.ruleMissing', { identity }) };
  }
  if (kind === 'a2a') {
    return {
      key: 'delegation',
      text: t('decisions.drawer.allowedBy.delegation', {
        rule: decision.rule,
        identity,
        target: decisionTarget(decision).texto,
      }),
    };
  }
  return {
    key: 'ruleAndContract',
    text: t('decisions.drawer.allowedBy.ruleAndContract', { rule: decision.rule, identity }),
  };
}

/**
 * A linha do veredito inteira: «Permitido por» no ALLOW; «Motivo» na negação, com a sua frase própria quando o PDP
 * não escreveu nenhuma; e, no indeterminado ou sem a linha da lista, só o motivo, quando há.
 */
function verdictLine(
  decision: Decision | undefined,
  reason: string | null | undefined,
): { key: VerdictCase; text: string | null } {
  if (decision?.verdict === 'permitido') return allowedBy(decision);
  const withReason = reason ? `${t('decisions.drawer.reasonLabel')}: ${reason}` : null;
  if (decision?.verdict === 'negado') {
    return withReason
      ? { key: 'reason', text: withReason }
      : { key: 'noReason', text: t('decisions.drawer.noReason') };
  }
  return { key: withReason ? 'reason' : 'none', text: withReason };
}

export interface DecisionDrawerProps {
  /** `?drawer=<request_id>`; `null` = fechado. */
  requestId: string | null;
  /**
   * A linha da lista, quando ela está carregada — é de onde sai o veredito.
   *
   * `DecisionDetail` do contrato **não** traz `veredito`: traz motivo,
   * autorização, identidade, pessoa e resultado. Quem afirma ALLOW/DENY é a
   * decisão da lista. Em link direto (sem a lista carregada) o drawer mostra o
   * detalhe sem o bloco de veredito, em vez de inventar um a partir do status
   * HTTP.
   */
  decision?: Decision;
  onClose: () => void;
}

export function DecisionDrawer({ requestId, decision, onClose }: DecisionDrawerProps) {
  const navigate = useNavigate();
  const detalhe = useDecision(requestId);
  const datum = detalhe.data;

  const autorizacao = datum?.authorization;
  const inferred = autorizacao?.verdict_inferred;

  /*
    O selo «inferido» fala sobre a PROCEDÊNCIA DO VEREDITO, não sobre o motivo —
    e até 04/09 ele aparecia em toda decisão, inclusive ALLOW (DEF-13).

    A causa: `verdict_inferred` vinha do detalhe do executor, que o preenchia
    com o veredito do próprio PDP — `"negado"`, `"permitido"`. Ler a presença
    dele como «foi inferido» transformava todo veredito lido em veredito
    adivinhado. Desde a parte 2 do aim-6ph.8 o detalhe sai do histórico, e o
    campo diz o que o nome diz: o veredito só quando o status do gateway o deu.

    Quem afirma a procedência é `veredito_fonte` da LINHA, e é ela que manda
    quando a lista está carregada. Em link direto não há linha, e aí vale a
    prova indireta: a frase do PDP (`reason`) só existe no `result` dele, então
    ter motivo é ter sido lido, não inferido.
  */
  const verdictFromGateway = decision
    ? verdictInferred(decision)
    : Boolean(inferred) && !autorizacao?.reason;
  const verdict = verdictLine(decision, autorizacao?.reason);
  // O estado do bloco Pessoa vem do detalhe (D14): ele vale também no link direto, sem a linha da lista.
  const personState = datum?.person_state ?? null;

  // ajustes-3 (28/08, item 13.2): a linha da lista já traz `identity_id` (resolvido
  // pelo slug) e `tool`; o detalhe pode chegar sem identidade (SPIFFE ID fora da
  // réplica, namespace diferente do atribuído) ou ainda estar carregando. Um cai
  // no outro — e a ação fica sempre visível: desabilitada, com o motivo, quando
  // não há identidade para simular.
  // Contexto das frases de «O que faria passar»: como a tela chama esta
  // identidade e quais claims o token trouxe. Sai do detalhe quando ele traz, e
  // da linha da lista quando não — o mesmo par que já resolve `identity_id`.
  // O slug primeiro, o SPIFFE ID como recurso: «Adicionar faturas.consultar ao
  // contrato de cobranca» é a frase acionável; com o SPIFFE ID inteiro ela vira
  // uma linha de log. Sem réplica, o SPIFFE ID é o único nome que existe.
  const identityText = decision
    ? decisionIdentity(decision).texto
    : (datum?.identity?.spiffe_id ?? null);
  const claimsText = decision
    ? decisionPerson(decision).texto
    : [datum?.user?.sub, datum?.user?.profile].filter(Boolean).join(' · ') || null;

  const identityId = datum?.identity_id ?? decision?.identity_id ?? null;
  const tool = datum?.request?.tool ?? decision?.tool ?? null;
  // DEF-34: numa delegação A2A a chamada citou um agente, não uma tool. O `kind`
  // vem da linha da lista; em link direto (sem linha) o detalhe não o traz, e a
  // decisão é lida como de tool — o que ela era até 04/09.
  const kind = decision ? decisionKind(decision) : null;
  const isA2A = kind === 'a2a';
  // DEF-29 (09/09): o Simulador passou a ter modo A2A, e uma delegação é
  // reproduzível como qualquer chamada. O aviso antigo — «o Simulador não avalia
  // delegações A2A» — descrevia um comportamento que mudou, que é o pior tipo de
  // ressalva: ensina errado e desabilita o botão certo.
  const agent = isA2A ? (decision?.agent ?? null) : null;
  const alvo = isA2A ? agent : tool;
  const canReproduzir = Boolean(identityId && alvo);
  // aim-78s: o que a decisão guardou da pessoa vai junto, e o Simulador abre no modo Claims. Nunca o token (regra 12).
  const claims = replayClaims(datum, decision);
  const reasonWithoutSimulator = !identityId
    ? t('decisions.drawer.simulate.noIdentity')
    : !alvo
      ? isA2A
        ? t('decisions.drawer.simulate.noAgent')
        : t('decisions.drawer.simulate.noTool')
      : undefined;

  return (
    <Drawer
      open={Boolean(requestId)}
      title={t('decisions.drawer.title', { id: requestId?.slice(0, 8) ?? '' })}
      subtitle={datum ? (dataHour(autorizacao?.decided_at) ?? undefined) : undefined}
      onClose={onClose}
      footer={
        <DrawerFooter>
          {/* PRD §4 T17: Ver identidade · Reproduzir no simulador. */}
          {identityId ? (
            <Link className={s.rodapeLink} to={`/identidades?drawer=${identityId}`}>
              {t('decisions.drawer.seeIdentity')}
            </Link>
          ) : null}
          <Button
            icon="play"
            disabled={!canReproduzir}
            title={reasonWithoutSimulator}
            aria-disabled={!canReproduzir || undefined}
            onClick={() =>
              // O simulador recebe identidade, alvo e claims por estado de rota: a URL do
              // simulador não carrega dado de uma decisão que pode sair da janela, nem dado
              // da pessoa. Numa delegação o alvo é o agente, e é ele que abre o modo A2A lá.
              navigate('/simulador', {
                state: {
                  ...(isA2A ? { identityId, agent } : { identityId, tool }),
                  ...(claims ? { claims } : {}),
                },
              })
            }
          >
            {t('decisions.drawer.simulate')}
          </Button>
        </DrawerFooter>
      }
    >
      {detalhe.isLoading ? <p className={s.vazio}>{t('decisions.drawer.loading')}</p> : null}

      {detalhe.isError ? (
        detalhe.error.code === 'detalhe_indisponivel' ? (
          <div data-testid="decision-not-found">
            <Alert tone="warning" title={t('decisions.drawer.unavailable.title')}>
              {t('decisions.drawer.unavailable.body')}
            </Alert>
          </div>
        ) : (
          <ErrorBanner
            error={detalhe.error}
            title={t('decisions.drawer.error')}
            onRetry={() => void detalhe.refetch()}
          />
        )
      ) : null}

      {/*
        Detalhe parcial (T5 do protótipo, §3.6). O detalhe sai só do histórico
        (D5), e a decisão guardada antes do aim-6ph.8 não tem os campos do
        detalhe. O aviso não é decoração: sem ele o fingerprint, o IP, os campos
        do token e a URL apareceriam vazios como se tivessem sido medidos e dado
        vazio. Dizer «não sei» é diferente de «nada».
      */}
      {datum?.detail_unavailable ? (
        <div data-testid="decision-partial-detail">
          <Alert tone="info" title={t('decisions.drawer.partial.title')}>
            {t('decisions.drawer.partial.body')}
          </Alert>
        </div>
      ) : null}

      {datum ? (
        <>
          <div className={s.vereditoBloco}>
            {decision ? (
              <strong
                className={[
                  s.vereditoGrande,
                  decision.verdict === 'permitido'
                    ? s.vereditoOk
                    : decision.verdict === 'negado'
                      ? s.vereditoDgr
                      : s.vereditoNeutro,
                ].join(' ')}
              >
                {verdictBadge(decision.verdict).rotulo}
              </strong>
            ) : null}
            <span
              className={s.vereditoTexto}
              data-testid="decision-verdict-text"
              data-case={verdict.key}
            >
              {verdict.text}
            </span>
            {verdictFromGateway ? (
              <Badge tone="warning" tip={inferredTip()}>
                {inferred
                  ? t('decisions.drawer.inferredWith', { source: inferred })
                  : t('decisions.badge.inferred')}
              </Badge>
            ) : null}
          </div>

          {/*
            «Por que negou», logo abaixo do veredito e ANTES de identidade,
            pessoa e pedido.

            A ordem é a pergunta que traz o operador aqui: ele já sabe que
            negou — o que ele veio buscar é onde parou. Identidade, vínculo e
            pedido são a prova, e prova vem depois da afirmação.

            Aparece também no ALLOW: ali o bloco diz por onde cada eixo passou,
            que é o que se confere quando o permitido é o surpreendente.
          */}
          <WhyDenied
            causa={datum.cause}
            diagnostico={datum.diagnostic}
            barreiras={datum.barriers}
            contexto={{
              tool: datum.request?.tool ?? decision?.tool ?? null,
              identity: identityText,
              claims: claimsText,
            }}
            kind={kind}
            agent={decision?.agent ?? null}
            verdict={decision?.verdict ?? null}
            simulatedBatch={Boolean(decision?.simulated && !decision.tool)}
            rpcMethod={datum.request?.rpc_method ?? decision?.rpc_method ?? null}
            controlCall={decision ? isControlCall(decision) : datum.control_call === true}
            notStored={Boolean(datum.detail_unavailable)}
          />

          <Section titulo={t('decisions.drawer.section.identity')}>
            {/*
              «Fonte» saiu (T13 do mrolim, 10/10): era uma frase fixa do executor, igual em toda decisão.
              O que ela explicava, de onde vem a identidade, virou a dica do SPIFFE ID.
            */}
            <Def
              rotulo={t('decisions.drawer.field.spiffeId')}
              valor={datum.identity?.spiffe_id}
              mono
              tip={t('decisions.drawer.field.spiffeId.tip')}
            />
            <Def
              rotulo={t('decisions.drawer.field.fingerprint')}
              valor={datum.identity?.sha256_fingerprint}
              mono
              tip={t('decisions.drawer.field.fingerprint.tip')}
            />
            <Def
              rotulo={t('decisions.drawer.field.originIp')}
              valor={datum.identity?.origin_ip}
              mono
              tip={t('decisions.drawer.field.originIp.tip')}
            />
          </Section>

          {/*
            O bloco Pessoa diz o ESTADO (D14 e T17 do mrolim, §3.5 e §6.3 do protótipo): pessoa verificada, sem
            token, token não verificado ou pessoa descrita numa simulação. Até aqui os três primeiros apareciam iguais,
            como «sem pessoa». Sem estado (a linha guardada antes do aim-6ph.8 e sem pessoa), o título fica só
            «Pessoa», e os campos dizem «não informado», com o aviso de detalhe parcial acima.
          */}
          <Section
            titulo={
              personState
                ? `${t('decisions.drawer.section.person')} · ${t(PERSON_STATE_KEY[personState])}`
                : t('decisions.drawer.section.person')
            }
            nota={
              personState && PERSON_STATE_TEXT[personState]
                ? t(PERSON_STATE_TEXT[personState])
                : undefined
            }
            testId="decision-person"
            state={personState ?? 'unknown'}
          >
            {personState === 'no_token' || personState === 'unverified' ? (
              // Sem pessoa, os campos do token não se aplicam (sem token) ou não foram lidos (o PDP não aceitou o
              // token): dizer qual dos dois é a diferença entre uma chamada de máquina e um token barrado.
              <>
                {[
                  'decisions.drawer.field.issuer',
                  'decisions.drawer.field.audience',
                  'decisions.drawer.field.expiresAt',
                  'decisions.drawer.field.binding',
                ].map((rotulo) => (
                  <Def
                    key={rotulo}
                    rotulo={t(rotulo)}
                    valor={
                      <span className={s.vazio}>
                        {t(
                          personState === 'no_token'
                            ? 'decisions.drawer.notApplicable'
                            : 'decisions.drawer.notRead',
                        )}
                      </span>
                    }
                  />
                ))}
              </>
            ) : null}
            {personState === 'described' ? (
              <>
                <Def rotulo={t('decisions.drawer.field.sub')} valor={datum.user?.sub} mono />
                <Def rotulo={t('decisions.drawer.field.profile')} valor={datum.user?.profile} />
              </>
            ) : null}
            {personState === 'verified' || !personState ? <PersonFields datum={datum} /> : null}
          </Section>

          <Section titulo={t('decisions.drawer.section.request')}>
            {isA2A ? (
              <Def
                rotulo={t('decisions.drawer.field.targetAgent')}
                tip={t('decisions.drawer.field.targetAgent.tip')}
                valor={
                  decision ? (
                    <span className={s.mono}>
                      <DecisionTargetCell decision={decision} />
                    </span>
                  ) : null
                }
              />
            ) : null}
            <Def rotulo={t('decisions.drawer.field.tool')} valor={datum.request?.tool} mono />
            <Def
              rotulo={t('decisions.drawer.field.rpcMethod')}
              valor={isA2A ? a2aMethod(datum.request?.rpc_method) : datum.request?.rpc_method}
              mono
            />
            {/* Sem «Método HTTP» (T10): o gateway só consulta o PDP por POST, e a linha não informava. */}
            <Def rotulo={t('decisions.drawer.field.url')} valor={datum.request?.url} mono />
          </Section>

          <Section titulo={t('decisions.drawer.section.authorization')}>
            <Def
              rotulo={t('decisions.drawer.field.decidedAt')}
              valor={dataHour(autorizacao?.decided_at)}
            />
            {/*
              O motivo não se repete aqui: ele é o bloco de veredito lá em cima. Sem «Consultas ao PDP» (T15):
              cada decisão do histórico é uma consulta, e o número era sempre 1.
            */}
          </Section>

          <Section titulo={t('decisions.drawer.section.result')}>
            <Def
              rotulo={t('decisions.drawer.field.httpStatus')}
              valor={datum.result?.status}
              mono
            />
            <Def
              rotulo={t('decisions.drawer.field.duration')}
              valor={
                datum.result?.duration_ms !== null && datum.result?.duration_ms !== undefined
                  ? `${Math.round(datum.result.duration_ms)} ms`
                  : null
              }
            />
            {/*
              Sem «Respondido em» (T11) e sem «Erro» (T12): a hora era o instante da decisão mais a duração do PDP,
              que não é a hora da resposta, e o erro era a mesma frase do motivo, no bloco do veredito.
            */}
          </Section>
        </>
      ) : null}
    </Drawer>
  );
}
